import { ChartSettingsT, OrderedColumnsT } from 'business/definitions';
import { Dataset } from '../../types';
import {
    extractSeriesKey,
    makeDataset,
} from 'components/ChartContainer/components/helpers/datasetHelpers';

import { EChartsOption } from 'echarts';
import { getColorsScale } from '../helpers/getColor';

const DEFAULT_ECHART_OPTIONS = {
    // Desactivate the animation for all chart for performance reason.
    animation: false,
};

/**
 * This is the base class for all EChart option-generator classes.
 * It provide a standard way to generate Echart {@link https://echarts.apache.org/en/option.html|option}
 * through the function {@link PolymorphicChartOption.getChartOption}.
 * Every Chart type should extend this class and the {@link PolymorphicChartOption.getChartOption}
 * to implement its own specific behaviour.
 * This class aim to be generic and will not provide a working `option` object, only one
 * with the common option to all the charts.
 */
class PolymorphicChartOption {
    protected orderedColumns: OrderedColumnsT;
    protected settings: ChartSettingsT;
    protected data: ResponseDataT[];
    protected width: number;
    protected height: number;
    protected dataset: Dataset[];

    constructor(
        orderedColumns: OrderedColumnsT,
        settings: ChartSettingsT,
        data: ResponseDataT[],
        width: number,
        height: number,
    ) {
        this.orderedColumns = orderedColumns;
        this.settings = settings;
        this.data = data;
        this.width = width;
        this.height = height;
        this.dataset = [];
    }

    /**
     * @returns the {@link https://echarts.apache.org/en/option.html|option} object to create an EChart chart.
     */
    getChartOption(): EChartsOption {
        this.dataset = this.getDataset();
        return {
            ...(this.dataset.length > 0 && { dataset: this.dataset }),
            ...DEFAULT_ECHART_OPTIONS,
            color: this.getColor(),
        };
    }

    /**
     * Construct the list of Dataset for this chart.
     * Use {@link makeDataset} behind the scene.
     * Does not handle yearly comparison, has this is not a common feature.
     * @returns The generated Dataset for this chart.
     */
    protected getDataset(): Dataset[] {
        const seriesKey = extractSeriesKey(this.data, this.orderedColumns);
        return makeDataset(
            this.data,
            this.orderedColumns,
            seriesKey,
            false,
            undefined,
            this.settings?.sortDimension,
        );
    }

    /**
     * Return the color scale for the chart.
     */
    protected getColor(): string[] {
        return getColorsScale(this.settings.palette);
    }
}

export default PolymorphicChartOption;
